import React, {
  Fragment,
  ChangeEvent,
  FormEvent,
  MouseEvent,
  ReactElement,
  useEffect,
  useState,
  useContext,
} from "react";
import useApiState, { api } from "../../../hooks/use-api-state";
import useInput from "../../../hooks/use-input";
import { IUserInfo } from "../../../messages/IAppInfo";
import { hasPermission } from "../../common/ProtectedRoute";
import { ICohortData } from "../../../messages/IGetCohortByIdResponse";
import { IGetCohortMemberData, IGetCohortMemberListResponse, IAttendantLoggedInData} from "../../../messages/IGetCohortMemberListResponse";
import { IGetCohortMemberRegistrationStatusResponse } from "../../../messages/IGetCohortMemberRegistrationStatusResponse";
import ErrorNotification from "../../common/ErrorNotification";
import NotificationsContext from "../../../store/notifications-context";
import Loading from "../../common/Loading";
import styles from "./CohortMembers.module.css";
import VerticalLayout from "../../common/VerticalLayout";
import ReactTooltip from "react-tooltip";

type CohortMemberRegistrationStatuses = { [email: string]: IGetCohortMemberRegistrationStatusResponse };

export default function CohortMembers(props: {
  userInfo: IUserInfo;
  cohort: ICohortData;
}): ReactElement {
  const notificationsContext = useContext(NotificationsContext);
  const [getCohortMemberListApiState, setGetCohortMemberListApiState] = useApiState(true);
  const [getMemberRegistrationStatusApiState, setGetMemberRegistrationStatusApiState] = useApiState(false);
  const [generateCertificateApiState, setGenerateCertificateApiState] = useApiState(false);
  const [sendRegistrationEmailApiState, setSendRegistrationEmailApiState] = useApiState(false);
  const [sendEvalformEmailApiState, setSendEvalformEmailApiState] = useApiState(false);
  const [sendCertificateEmailApiState, setSendCertificateEmailApiState] = useApiState(false);
  const [sendAdultTrainingHuInfoEmailApiState, setSendAdultTrainingHuInfoEmailApiState] = useApiState(false);
  const [addMemberApiState, setAddMemberApiState] = useApiState(false);
  const [getCertificateLinkForUserApiState, setGetCertificateLinkForUserApiState] = useApiState(false);
  const [getAllCertificateLinksForUserApiState, setGetAllCertificateLinksForUserApiState] = useApiState(false);

  const [subcohortList, setSubcohortList] = useState<string[]>([]);
  const [memberData, setMemberData] = useState<{[user_id: string]: IGetCohortMemberData}>({}); // Stores all detailed data for each member, indexed by user_id
  const [memberIds, setMemberIds] = useState<string[]>([]); // list of all user_ids, used to iterate through all members (while subcohortMembers is used to render the page)
  const [subcohortMembers, setSubcohortMembers] = useState<{ [subcohortId: string]: string[] }>({}); // member_ids_by_subcohort
  
  const [checkedMemberIds, setCheckedMemberIds] = useState<Set<string>>(new Set());
  const [, setCohortMemberRegistrationStatuses] = useState<CohortMemberRegistrationStatuses | null>(null);
  const [selectedUserUserId, setSelectedUserUserId] = useState<string | null>(null);
  const [draggedUserId, setDraggedUserId] = useState<string | null>(null);
  const [dragOverelement, setDragOverElement] = useState<HTMLElement | null>(null);
  const [selectedUserCertificateLink, setSelectedUserCertificateLink] = useState<string | null>(null);
  const [addUserCount, setAddUserCount] = useState<number>(0);
  const [addedUserNumber, setAddedUserNumber] = useState<number>(0);
 
  const emailInput = useInput(
    "",
    (value: string) => {
      return value.match(".+@.+\\..+") !== null;
    },
    "cy__err"
  );


  useEffect(() => {
    processMemberList(null);
    let subcohortIds = [""];
    for(let subcohort of props.cohort.subcohorts ?? []) {
      subcohortIds.push(subcohort.id);
    }
    setSubcohortList(subcohortIds);
    setCheckedMemberIds(new Set());

    api.getCohortMemberList(
        setGetCohortMemberListApiState,
        props.cohort.id)
      .then((memberList: IGetCohortMemberListResponse) => {
        processMemberList(memberList);
      });

    setSelectedUserUserId(null);
  }, [props.cohort.id, props.cohort.subcohorts/*, setGetCohortMemberListApiState*/]);


  async function processMemberList(memberList: IGetCohortMemberListResponse | null) {
    setMemberData(memberList?.member_data ?? {});
    setMemberIds(memberList?.memberids ?? []);
    setSubcohortMembers(memberList?.member_ids_by_subcohort ?? {});
    setCheckedMemberIds(new Set());
  }


  async function addUsersSubmitHandler(event: FormEvent<HTMLFormElement>) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    try {
      let unpackedCohortMembers = await api.unpackCohortMembers(
        setAddMemberApiState,
        props.cohort.id,
        emailInput.value.split("\n")
      )
      let list = "";
      for (let i = 0; i < unpackedCohortMembers.length; ++i) {
        const name = unpackedCohortMembers[i].username;
        const fullname = unpackedCohortMembers[i].fullname;
        list = list + `${name} ${fullname ? "("+fullname+")" : ""}\n`;
      }
      let count = unpackedCohortMembers.length;
      setAddedUserNumber(0);
      setAddUserCount(count);
      if (window.confirm(`Do you really want add the following ${count} users?\n` + list)) {
        let addedMembers = [];
        let addedMemberIds = [];
        for (let i = 0; i < count; ++i) {
          let member = unpackedCohortMembers[i];
          let response = await api.addCohortUser(
            setAddMemberApiState,
            props.cohort.id,
            member,
            count === 1);
          emailInput.reset("");
          setAddedUserNumber(i+1);
          if (response.status === "ok") {
            let user_id = response.user_id;
            addedMembers.push(member.username);
            addedMemberIds.push(user_id);
            // We only get back the memberList if cohort was updated (count==1)
            if (response.memberlist) {
              processMemberList(response.memberlist);
            }
          }
        }
        // If there were several users to add, we update the cohort only at the end
        if (count>1 && addedMemberIds.length>0) {
          let response = await api.addCohortMembers(
            setAddMemberApiState,
            props.cohort.id,
            addedMemberIds);
          if (response.status==="ok" && response.memberlist) {
            processMemberList(response.memberlist);
          }  
        }
      }
      setAddUserCount(0);
    } catch (e: any) {
      processMemberList(null);
    }
  }

  async function userSelectedHandler(user_id: string|null) {
    if (selectedUserUserId === user_id) {
      setSelectedUserUserId(null);
      return;
    }
    setSelectedUserUserId(user_id);
    if (user_id) {
      try {
        const certificateLink =
          await api.getCertificateLinkForUser(
            setGetCertificateLinkForUserApiState,
            props.cohort.id,
            user_id
          );

        setSelectedUserCertificateLink(certificateLink?.link);
      } catch (e: any) {
        //TODO
      }
    }
  }


  function getMemberData(user_id:string|null=null) {
      let member_data = null;
      member_data = memberData[user_id??""] ?? null;
      return member_data;
  }


  function resetAction() {
    for (const user_id of memberIds??[]) {
      const member_data = getMemberData(user_id);
      if (member_data) {
        member_data.action_accomplished = false;
        member_data.action_errored = false;
      }
    }
  }


  function numberOfCheckedUser() {
    let noneChecked = (checkedMemberIds.size === 0);
    if (noneChecked) {
      return selectedUserUserId ? 1 : 0;
    }
    else {
      return checkedMemberIds.size;
    }
  }


  function userIsChecked(user_id:string) {
    let noneChecked = (checkedMemberIds.size === 0);
    if (noneChecked) { // If none is checked, we take the selected one
      return user_id === selectedUserUserId;
    }
    else {
      return checkedMemberIds.has(user_id);
    }
  }


  async function generateCertificateForCheckedUsers() {
    resetAction();
    const initialNumberOfCheckedItems = checkedMemberIds.size;
    var selectEmail = null;
    for (const user_id of memberIds??[]) {
      const member_data = getMemberData(user_id);
      if (member_data && userIsChecked(user_id)) {
        try {
          const response =
            await api.generateCertificateForUser(
              setGenerateCertificateApiState,
              props.cohort.id,
              user_id
            );
          if (response.status === "ok") {
            member_data.certificate_of_attendance_generated = true;
            member_data.action_accomplished = true;
          } else {
            member_data.action_errored = true;
          }
          if (initialNumberOfCheckedItems === 1 || user_id === selectedUserUserId) {
            selectEmail = user_id;
          }
        } catch (e: any) { }
      }
    }
    if (selectEmail) { //(Re)select to refresh the certificate on the screen
      userSelectedHandler(selectEmail??null);
    }
  }


  async function sendRegistrationEmail() {
    if (memberIds !== null) {
      resetAction();
      let noneChecked = (checkedMemberIds.size === 0);

      let to_poll = [];
      for (const user_id of memberIds??[])
        if (userIsChecked(user_id)) {
          to_poll.push(user_id)
        }

      let response = await api.pollRegistrationEmail(
        setSendRegistrationEmailApiState,
        to_poll,
        props.cohort.id,
        true /*!noneChecked*/
      );

      const send_to = response.sending_to;
      let send_count = 0;
      if (send_to.length > 0) {
        let sendlist = "";
        for (const user_id of memberIds) {
          const member_data = getMemberData(user_id);
          if (send_to.includes(user_id)) {
            send_count++;
            sendlist = sendlist + "-" + member_data?.username + "\n";
          }
        }

        /* TODO: most probably React has a better modal for this */
        if (window.confirm(`Do you really want to (re)send registration email to the following ${send_count} users?\n` + sendlist)) {
          for (const user_id of memberIds) {
            if (send_to.includes(user_id)) {
              try {
                const response =
                  await api.sendRegistrationEmail(
                    setSendRegistrationEmailApiState,
                    user_id,
                    props.cohort.id,
                    !noneChecked
                  );
                const member_data = getMemberData(user_id);
                if (member_data) {
                  if (response.status === "registration_sent_ok") {
                    member_data.sent_registration_email = Math.floor(Date.now() / 1000);
                    member_data.action_accomplished = true;
                  }
                  else if (response.status === "new_cohort_sent_ok") {
                    member_data.sent_new_cohort_email = Math.floor(Date.now() / 1000);
                    member_data.action_accomplished = true;
                  }
                  else if (response.status === "already_registered") {
                    member_data.registered = true;
                  }
                  else if (response.status === "error") {
                    addMemberApiState.errorMessage = "Error occurred";
                    member_data.action_errored = true;
                  }
                }
              } catch (e: any) { }
            }
          }
        }
      }
    }
  }


  async function sendEvalformEmail() {
    resetAction();
    //let noneChecked = (checkedMemberIds.size === 0);

    let usersIds = [];
    for (const user_id of memberIds??[])
      if (userIsChecked(user_id))
        usersIds.push(user_id)

    let response = await api.pollEvalformEmail(
      setSendEvalformEmailApiState,
      props.cohort.id,
      usersIds,
      true, /*!noneChecked,*/
    );

    const statuses = response.statuses;
    if (Object.keys(statuses).length > 0) {
      let sendlist = "";
      let resendlist = "";
      let send_count = 0;
      let resend_count = 0;
      let send_to = [];
      for (const user_id of memberIds??[]) {
        const member_data = getMemberData(user_id);
        const status = statuses[user_id] || null;
        if (status != null) {
          if (status === "not_sent") {
            send_count++;
            send_to.push(user_id);
            sendlist = sendlist + "- " + member_data?.username + "\n";
          }
          else if (status === "already_sent") {
            resend_count++;
            send_to.push(user_id);
            const member_data = getMemberData(user_id);
            resendlist = resendlist + "- " + member_data?.username + "\n";
          }
        }
      }

      if ((send_count + resend_count) === 0) {
        alert(`No users to send emails to.`);
        return;
      }

      let modalMessage = "";
      if (send_count > 0)
        modalMessage = modalMessage + `The evaluation form email will be sent to the following ${send_count} users:\n${sendlist}`;
      if (resend_count > 0)
        modalMessage = modalMessage + `\nThe evaluation form REMINDER email will be sent to the following ${resend_count} users:\n${resendlist}`;
      modalMessage = modalMessage + `\nDo you want to proceed?`;
      //TODO: most probably React has a better modal for this */
      if (window.confirm(modalMessage)) {
        const response = await api.sendEvalformEmails(
          setSendEvalformEmailApiState,
          props.cohort.id,
          send_to
        );
        const statuses = response.statuses;
        for (const user_id of memberIds??[]) {
          const status = statuses[user_id] || null;
          const member_data = getMemberData(user_id);
          if (member_data) {
            if (status === "sent_ok" || status === "resent_ok") {
                member_data.sent_evalform = Math.floor(Date.now() / 1000);
              member_data.action_accomplished = true;
            }
            else if (status !== null) {
              member_data.action_errored = true;
            }
          }
        }
      }
    }
  }



  async function sendCertificateEmail() {
    resetAction();
    let noneChecked = (checkedMemberIds.size === 0);

    let usersIds = [];
    for (const user_id of memberIds??[])
      if (userIsChecked(user_id))
        usersIds.push(user_id)

    let response = await api.pollCertificateEmail(
      setSendCertificateEmailApiState,
      props.cohort.id,
      usersIds,
      !noneChecked,
    );

    const statuses = response.statuses;
    if (Object.keys(statuses).length > 0) {
      let sendlist = "";
      let nosendlist = "";
      let ok = 0;
      let send_to = [];
      for (const user_id of memberIds??[]) {
        const member_data = getMemberData(user_id);
        const status = statuses[user_id] || null;
        if (status != null) {
          if (status === "ok") {
            ok++;
            send_to.push(user_id);
            sendlist = sendlist + "- " + member_data?.username + "\n";
          }
          else {
            nosendlist = nosendlist + "- " + member_data?.username + " (" + status + ")\n";
          }
        }
      }

      if (ok === 0) {
        alert(`No users to send emails to.`);
        return;
      }

      let modalMessage = `Do you really want to (re)send certificate email to the following ${ok} users?\n${sendlist}`;
      if (nosendlist !== "")
        modalMessage = modalMessage + `\nThe following users will not receive the email (see reson in brackets):\n${nosendlist}`;
      //TODO: most probably React has a better modal for this */
      if (window.confirm(modalMessage)) {
        const response = await api.sendCertificateEmails(
          setSendCertificateEmailApiState,
          props.cohort.id,
          send_to
        );
        const statuses = response.statuses;
        for (const user_id of memberIds??[]) {
          const status = statuses[user_id] || null;
          const member_data = getMemberData(user_id);
          if (member_data) {
            if (status === "sent_ok") {
              member_data.certificate_of_attendance_sent = true;
              member_data.action_accomplished = true;
            }
            else if (status !== null) {
              member_data.action_errored = true;
            }
          }
        }
      }
    }
  }


  async function sendAdultTrainingHuInfoEmail() {
    resetAction();

    let usersIds = [];
    for (const user_id of memberIds??[])
      if (userIsChecked(user_id))
        usersIds.push(user_id);

    let response = await api.pollAdultTrainingHuInfoEmail(
      setSendAdultTrainingHuInfoEmailApiState,
      props.cohort.id,
      usersIds,
      true /*!noneChecked,*/
    );

    const statuses = response.statuses;
    if (Object.keys(statuses).length > 0) {
      let sendlist = "";
      let nosendlist = "";
      let ok = 0;
      let send_to = [];
      for (const user_id of memberIds??[]) {
        const member_data = getMemberData(user_id);
        const status = statuses[user_id] || null;
        if (status != null) {
          if (status === "ok") {
            ok++;
            send_to.push(user_id);
            sendlist = sendlist + "- " + member_data?.username + "\n";
          }
          else {
            nosendlist = nosendlist + "- " + member_data?.username + " (" + status + ")\n";
          }
        }
      }

      if (ok === 0) {
        alert(`No users to send emails to.`);
        return;
      }

      let modalMessage = `Do you really want to (re)send adult education info gathering email to the following ${ok} users?\n${sendlist}`;
      if (nosendlist !== "")
        modalMessage = modalMessage + `\nThe following users will not receive the email (see reson in brackets):\n${nosendlist}`;
      //TODO: most probably React has a better modal for this */
      if (window.confirm(modalMessage)) {
        const response = await api.sendAdultTrainingHuInfoEmail(
          setSendAdultTrainingHuInfoEmailApiState,
          props.cohort.id,
          send_to
        );
        const statuses = response.statuses;
        for (const user_id of memberIds??[]) {
          const status = statuses[user_id] || null;
          const member_data = getMemberData(user_id);
          if (member_data) {
            if (status === "sent_ok") {
                member_data.sent_far_info = Math.floor(Date.now() / 1000);
              member_data.action_accomplished = true;
            }
            else if (status !== null) {
              member_data.action_errored = true;
            }
          }
        }
      }
    }
  }


  async function checkMembersRegistration(
    event: MouseEvent<HTMLButtonElement>,
  ) {
    resetAction();
    for (const user_id of memberIds??[]) {
      if (userIsChecked(user_id)) {
        const member_data = getMemberData(user_id);
        const email = member_data?.email ?? "";
        try {
          let cohortMemberRegistrationStatus =
            await api.getCohortMemberRegistrationStatus(
              setGetMemberRegistrationStatusApiState,
              user_id,
              props.cohort.id,
            );

          setCohortMemberRegistrationStatuses((state: CohortMemberRegistrationStatuses | null) => {
            if (state === null) {
              state = {};
            }

            let ret = { ...state };
            ret[email] = cohortMemberRegistrationStatus;
            //Update memberList.data with the received information
            // TODO: MAT-366: CohortMembers.tsx memberList should not be written directly
            if (member_data) {
              member_data.registered = cohortMemberRegistrationStatus.registrationOk;
              member_data.has_lmodule_data = true;
              member_data.lmodules_ok = cohortMemberRegistrationStatus.lmodulesOk;
              member_data.attendant_version_ok = cohortMemberRegistrationStatus.attendantversionOk;
            }
            return ret;
          });
        } catch (e: any) { }
      }
    }
  }


  function checkboxChangeHandler(user_id: string, e: ChangeEvent<HTMLInputElement>) {
    const newSet = new Set(checkedMemberIds);
    if (e.target.checked) {
      newSet.add(user_id);
    } else {
      newSet.delete(user_id);
    }
    setCheckedMemberIds(newSet);
  }


  function selectAllClickHandler(e: MouseEvent<HTMLButtonElement>) {
    let newSet = new Set<string>();
    if (checkedMemberIds.size === 0) {
      for (const user_id of memberIds??[]) {
        newSet.add(user_id);
      }
    }
    else {
      setSelectedUserUserId(null);
    }
    setCheckedMemberIds(newSet);
  }


  async function resendRegistrationLink(user_id:string, event: MouseEvent<HTMLImageElement>|MouseEvent<HTMLDivElement>|null=null) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    const member_data = getMemberData(user_id);
    if (member_data) {
      let cohortMemberRegistrationStatus = await api.getCohortMemberRegistrationToken(
        setGetMemberRegistrationStatusApiState,
        member_data?.user_id,
        props.cohort.id
      );
      const link = cohortMemberRegistrationStatus?.registration_link;
      if (link)
        member_data.registration_link = link;

      if (link) {
        let subject = "Resending your registration link for CydrillSergeant.com from my account";
        let body = `Dear ${member_data.fullname ?? "participant"},\n\nJust a quick resend of your registration link - please find it here:\n${link}\n\nBest regards,\n${props.userInfo.fullname}`;
        let url = `mailto:${member_data.email ?? ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.open(url, '_self');
      }
      else {
        alert("Error: no registration link.");
      }
    }
  }

  function isObsolete(active_since: string|null|undefined, active_until:string|null|undefined) {
    if (active_since === null || active_since === undefined || active_until === null || active_until === undefined) {
      return "";
    }
    const now = new Date(); // UTC or not, but we just compare dates anyway
    const since = new Date(active_since);
    const until = new Date(active_until);
    if (now < since) {
      return true;
    }
    if (now > until) {
      return true;
    }
    return false;
  }


  function notify(message: string) {
    notificationsContext.addNotification({ message: message });
  }

 
  function copyToClipboard(value:string|null|undefined, message:string) {
    if (value) {
      navigator.clipboard.writeText(value);
      if (message) {
        notify(message);
      }
    }
  }


  function copyAllEmailsToClipboard() {
    let list = "";
    for (const user_id of memberIds??[]) {
      const member_data = getMemberData(user_id);
      const link = member_data?.registration_link ?? "";
      if (link) {
        const line = `${member_data?.email ?? ""}\t${link}`;
        list = list + line + "\n";
      }
    }
    copyToClipboard(list,"The registration links have been copied to the clipboard.");
  }

  async function copyAllCertificatesToClipboard() {
    try {
      const certificateList =
        await api.getCertificateLinksForCohort(
          setGetAllCertificateLinksForUserApiState,
          props.cohort.id,
        );
        const links = certificateList.links;
        let list = "Email\tName\tCertificate link\n";
        if (links) {
          for (const user_id of memberIds??[]) {
            const member_data = getMemberData(user_id);
            const link = links[member_data?.user_id ?? ""] ?? "";
            list = list + `${member_data?.email ?? ""}\t${member_data?.fullname ?? ""}\t${link}\n`;
          }
        }
        copyToClipboard(list,"The certificate links have been copied to the clipboard.");
    } catch (e: any) { }
  }


  function copyAllFarDataToClipboard() {
    let list = "";
    //let line="Legmagasabb iskolai végzettsége\tViselt neve\tSzületési neve\tAnyja neve\tSzületési ország\tSzületési helye\tSzületési ideje\tE-mail címe\tMagyarországi lakcímmel nem rendelkező nem magyar állampolgár\n";
    for (const user_id of memberIds??[]) {
      const member_data = getMemberData(user_id);
      const far_data = member_data?.far ?? {};
      let line = `\t${member_data?.fullname??""}\t\t\t\t\t\t\t${member_data?.email??""}\t`;
      if (far_data) {
        line = line = `${far_data.iskolaiVegzettsege??""}\t`+
                      `${member_data?.fullname??""}\t`+
                      `${far_data.szuletesiNeve??""}\t`+
                      `${far_data.anyjaNeve??""}\t`+
                      `${far_data.szuletesiOrszag??""}\t`+
                      `${far_data.szuletesiHelye??""}\t`+
                      `${far_data.szuletesiIdeje??""}\t`+
                      `${member_data?.email??""}\t`+
                      `${far_data.nemMagyarAllampolgar ? "Igen" : "Nem"}\t` + 
                      `Nem`;
      }
      list = list + line + "\n";
    }
    copyToClipboard(list,"The FAR data has been copied to the clipboard.");
  }


  function getRegisteredNumber() {
    let registeredNum = 0;
    for (const user_id of memberIds??[]) {
      const member_data = getMemberData(user_id);
      if (member_data?.registered)
        registeredNum++;
    }
    return registeredNum;
  }

  
  function getLoginStatusTitle(member_data:IGetCohortMemberData) : string {
    
    function addToTitle(ret:string, s:string): string {
      if (ret.length>0) {
        return ret += "<br/>" + s;
      }
      else {
        return ret += s;
      }
    }

    const loggedInData:IAttendantLoggedInData| null = member_data.attendant_logged_in??null;
    let ret = "";
    if (member_data.registered) {
      const reg_date_time = member_data.registration_time ? new Date(member_data.registration_time * 1000) : null;
      ret = addToTitle(ret, `The user is registered${reg_date_time ? " at " + reg_date_time.toLocaleString() : ""}.`);
      if (loggedInData) {
        ret = addToTitle(ret, `The user successfully logged in to Attendant${loggedInData?.attendant_version ? " v"+loggedInData?.attendant_version : ""}.`);
        if (loggedInData.image_id) {
          ret = addToTitle(ret, `Image: ${loggedInData.image_id} (${loggedInData.virtualization})`);
        }
        if (loggedInData.instance_id && loggedInData.instance_id!=="local") {
          ret = addToTitle(ret, `Instance ID: ${loggedInData.instance_id}`);
        }
        if (loggedInData.logged_in_at) {
          const date_time = new Date(loggedInData.logged_in_at * 1000);
          ret = addToTitle(ret, `Last login: ${date_time.toLocaleString()}`);
        }
      }
      if (member_data.sent_new_cohort_email) {
        const date_time = new Date(member_data.sent_new_cohort_email * 1000);
        ret = addToTitle(ret, `The new cohort assignment email has been sent${date_time ? " at " + date_time.toLocaleString() : ""}.`);
      }
      // This is put here always - even if the user is registered, it might be that they did not receive the registration email
      ret = addToTitle(ret, "Click here to resend the registration email from your account.");
    }
    return ret;
  }


  function getVirtualizationType(virtualization?:string) : string {
    let ret = "";
    if (virtualization) {
      if (virtualization.toLowerCase().startsWith("v"))
          ret = "VBX";
      else if (virtualization.toLowerCase().startsWith("h"))
          ret = "HYV";
      if (virtualization.toLowerCase().startsWith("am"))
          ret = "AWS";
      if (virtualization.toLowerCase().startsWith("az"))
          ret = "AZU";
    }
    return ret;
  }


  function getSubCohortName(subcohort:string) : string {
    if (props.cohort.subcohorts) {
      const subcohort_data = props.cohort.subcohorts.find((s) => s.id === subcohort);
      if (subcohort_data) {
        return subcohort_data.name ?? subcohort_data.name;
      }
    }
    return "Default";
  }


  function splitSubcohortId(id:string) : {subcohort:string, user_id:string} {
    let subcohort = "";
    let user_id = "";
    if (id) {
      const parts = id.split("/");
      subcohort = parts[0] ?? "";
      user_id = parts[1] ?? "";
    }
    return { subcohort, user_id };
  }


  function dragOver(event: React.DragEvent<HTMLSpanElement>) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    let element = event.currentTarget.closest("li");
    if (!element)
      return false;
    let id = element.dataset.id;
    const { subcohort, user_id } = splitSubcohortId(id ?? "");
    const allow = !!subcohort && !!draggedUserId && draggedUserId !== user_id;
    if (dragOverelement && dragOverelement !== element) {
      dragOverelement.classList.remove(styles.dragover);
    }
    if (allow) {
      element.classList.add(styles.dragover);
      setDragOverElement(element);
    }
    return allow;
  }

  async function endDrag(event: React.MouseEvent<HTMLSpanElement>) {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    let element = event.currentTarget.closest("li");
    if (!element)
      return false;
    let id = element.dataset.id;
    let { subcohort, user_id } = splitSubcohortId(id ?? "");
    if (dragOverelement) {
      dragOverelement.classList.remove(styles.dragover);
    }
    setDragOverElement(null);
    if (subcohort && draggedUserId) {
      if (subcohort === "(none)")
        subcohort = "";
      let response = await api.putUserToSubcohort(
        setAddMemberApiState,
        props.cohort.id,
        draggedUserId,
        subcohort,
        user_id
      );
      if (response.status==="ok" && response.memberlist) 
        processMemberList(response.memberlist);
    }
  }

  return (
    <>
      <div className="cy__form form__certificates">
        <VerticalLayout
          keyForStorage="cohortMembers"
          topPanel={
            <>
              <ReactTooltip id="tooltip" html={true} className={styles.tooltip}/>
              <div className={`members ${styles.top__panel}`}>
                <div className={styles.members}>
                  <div className={styles.members_list_div}>
                    <div className={styles.members_list_header}>
                      <button
                        type="submit"
                        className={`btn secondary ${styles.selectall}`}
                        onClick={selectAllClickHandler}
                        /*disabled={checkedMemberIds.size ===  0}*/
                      >
                        {checkedMemberIds.size === 0 ? <>Select all</> : <>Select none</>}
                      </button>
                      <h3>Members{!addMemberApiState.isLoading && memberIds?.length>0 && <span className={styles.registered}>({getRegisteredNumber()} / {memberIds?.length} registered)</span>}</h3>
                    </div>
                    <ul className={styles.memberlist}>
                      <Loading isLoading={getCohortMemberListApiState.isLoading}>
                        {!!getCohortMemberListApiState.errorMessage ? (
                          <ErrorNotification
                            errorMessage={getCohortMemberListApiState.errorMessage}
                          />
                        ) : (
                          <>
                            {subcohortList.map((subcohort: string) => {
                              let memberids = subcohortMembers[subcohort] ?? [];
                              //console.log(`${subcohort}: ${JSON.stringify(memberids)}`)
                              return (
                                <Fragment key={subcohort}>
                                  {props?.cohort?.ctf && (
                                  <li key={subcohort}
                                      className={styles.subcohort}
                                      data-id={(subcohort!=="" ? subcohort : "(none)")+"/"}
                                      onDragOver={(event) => dragOver(event)}
                                      onDrop={(event) => endDrag(event)}
                                  >
                                      <span>{getSubCohortName(subcohort)}</span>
                                  </li>
                                  )}
                                  {memberids.map((user_id: string, index) => {
                                    const member_data = getMemberData(user_id);
                                    if (!member_data) 
                                      { return <li key={user_id}></li>; }
                                    else
                                    {
                                      return (
                                        <li key={user_id}
                                            className={`${selectedUserUserId === user_id ? "active" : ""} ${isObsolete(member_data?.active_since, member_data?.active_until) ? styles.obsolete : ""}`}
                                            data-id={(subcohort!=="" ? subcohort : "(none)")+"/"+user_id}
                                            onDragOver={(event) => dragOver(event)}
                                            onDrop={(event) => endDrag(event)}
                                        >
                                          <ReactTooltip id={`member-tooltip-${user_id}`} html={true} className={styles.tooltip}/>
                                          {(props?.cohort?.subcohorts?.length>0) && (
                                          <span
                                            className={styles.grabber}
                                            draggable
                                            onDragStart={() => setDraggedUserId(user_id)}
                                          >⠿</span>
                                          )}
                                          <input
                                            className={styles.membercheckbox}
                                            type="checkbox"
                                            checked={checkedMemberIds.has(user_id)}
                                            onChange={(e) => checkboxChangeHandler(user_id, e)}
                                          >
                                          </input>

                                          <span onClick={() => userSelectedHandler(user_id)}>
                                            <span className={styles.attendant_version}>v{member_data?.attendant_version ?? ""}</span>

                                            <span>
                                              <div className={styles.member_status_div}>
                                                {member_data.registered ?
                                                  ( // Registered
                                                    !!member_data.attendant_logged_in ?
                                                    (
                                                      <div className={styles.attendant_logged_in_div}
                                                          data-for={`member-tooltip-${user_id}`}
                                                          data-tip={getLoginStatusTitle(member_data)}
                                                          onClick={(e) => { resendRegistrationLink(user_id, e); }}
                                                      >
                                                        <img
                                                          src="/icons/cydrill_attendant_favicon.png"
                                                          className={`${styles.membericon} ${styles.attendant_icon}`}
                                                          alt=""
                                                        />
                                                        <span className={styles.virtualization}>
                                                          {getVirtualizationType(member_data.attendant_logged_in?.virtualization)}
                                                        </span>
                                                      </div>
                                                    )
                                                    :
                                                    (
                                                      <img
                                                        src="/icons/CydrillSergeant_favicon.png"
                                                        className={`${styles.membericon} ${styles.membericon_registration_status}`}
                                                        data-for={`member-tooltip-${user_id}`}
                                                        data-tip={getLoginStatusTitle(member_data)}
                                                        onClick={(e) => { resendRegistrationLink(user_id, e); }}
                                                        alt=""
                                                      />
                                                    )
                                                  ) :
                                                  ( // Not registered
                                                    !!member_data.sent_registration_email ? (
                                                      <img
                                                        src="/icons/email_40x40_2.png"
                                                        className={`${styles.membericon} ${styles.membericon_registration_status}`}
                                                        data-for={`member-tooltip-${user_id}`}
                                                        data-tip={`The registration email has been sent at ${new Date(member_data.sent_registration_email * 1000).toLocaleString()}.<br/>Click here to resend the registration link from your account.`}
                                                        onClick={(e) => { resendRegistrationLink(user_id, e); }}
                                                        alt=""
                                                      />
                                                    ) : (
                                                      <>
                                                        <span
                                                          className={`${styles.membericon} ${styles.membericon_registration_status}`}
                                                          />
                                                      </>
                                                    )
                                                  )
                                                }
                                              </div>
                                            </span>

                                            <>
                                              {member_data?.registered && member_data?.has_lmodule_data ? (
                                                <>
                                                  <>
                                                    {member_data?.lmodules_ok &&
                                                      member_data?.attendant_version_ok ? (
                                                      <img
                                                        src="/icons/testing_A_40x40_2.png"
                                                        className={styles.membericon}
                                                        data-for={`member-tooltip-${user_id}`}
                                                        data-tip="The sergeant version and the learning module list is is correct."
                                                        alt=""
                                                      />
                                                    ) : (
                                                      <img
                                                        src="/icons/error_40x40.png"
                                                        className={styles.membericon}
                                                        data-for={`member-tooltip-${user_id}`}
                                                        data-tip={
                                                          !member_data.attendant_version_ok
                                                            ? "Sergeant version is not set correctly. "
                                                            : "" +
                                                              !member_data.lmodules_ok
                                                              ? "Learning module list is not correct. "
                                                              : ""
                                                        }
                                                        alt=""
                                                      />
                                                    )}
                                                  </>
                                                </>
                                              ) : null}
                                            </>

                                            <span className={styles.member_name}>
                                              {member_data?.fullname ? (
                                                <span>
                                                  {member_data?.email} ({member_data.fullname})
                                                </span>
                                              ) : (
                                                <span>
                                                  {member_data?.email}
                                                </span>
                                              )}
                                            </span>

                                            <div className={styles.copy_user_div}
                                                data-for={`member-tooltip-${user_id}`}
                                                data-tip="Copy email address to clipboard."
                                                onClick={() => {
                                                  copyToClipboard(member_data?.username,`The ${member_data?.username} email address has been copied to the clipboard.`);
                                                }}>
                                              <img
                                                src="/icons/copy.svg"
                                                className={`${styles.membericon} ${styles.member_clipboard_icon}`}
                                                alt=""
                                              />
                                              <div className={styles.overlay_text}>@</div>
                                            </div>
                                            <div className={styles.copy_user_div}
                                                data-for={`member-tooltip-${user_id}`}
                                                data-tip="Copy user ID to clipboard."
                                                onClick={() => {
                                                  copyToClipboard(user_id,`The UUID of ${member_data?.username} (${user_id}) has been copied to the clipboard.`);
                                                }}>
                                              <img
                                                src="/icons/copy.svg"
                                                className={`${styles.membericon} ${styles.member_clipboard_icon}`}
                                                alt=""
                                              />
                                              <div className={styles.overlay_text}>ID</div>
                                            </div>

                                            {member_data?.company &&
                                              <span className={styles.member_company}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip="Company name set in the user profile.">
                                                {member_data.company}
                                              </span>
                                            }

                                            {!props?.cohort?.ctf && member_data?.subcohort &&
                                              <span className={styles.member_subcohort}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip="Subcohort set in the user profile.">
                                                /{member_data.subcohort}
                                              </span>
                                            }

                                            {member_data?.user_lmodules &&
                                              <span className={styles.member_lmodules}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip="User's learning modules.">
                                                [{member_data.user_lmodules.join(", ")}]
                                              </span>
                                            }

                                            {member_data?.region && (
                                              <span className={styles.user_region}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip="User's region.">Region: <b>{member_data.region}</b></span>
                                            )}

                                            {member_data?.active_since && (
                                              <span className={styles.memberactivation}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip={`User is active since ${member_data.active_since_time}.`}
                                              >{member_data.active_since}</span>
                                            )}
                                            {member_data?.active_until && (
                                              <span className={styles.memberactivation}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip={`User is active until ${member_data.active_until_time}.`}
                                              >- {member_data.active_until}</span>
                                            )}

                                            <>
                                              {member_data?.sent_evalform && (
                                                <a>
                                                  <img
                                                    src="/icons/rule_of_thumb_40x40.png"
                                                    className={styles.membericon_after}
                                                    data-for={`member-tooltip-${user_id}`}
                                                    data-tip={`The course evaluation form has been sent to the user${member_data.sent_evalform ? " at " + new Date(member_data.sent_evalform * 1000).toLocaleString() : ""}.<br/>Click here to resend a reminder from your personal email account.`}
                                                    alt=""
                                                  />
                                                </a>
                                              )}
                                            </>

                                            <>
                                              {member_data?.certificate_of_attendance_generated && (
                                                <img
                                                  src="/icons/gamification_40x40.png"
                                                  className={styles.membericon_after}
                                                  data-for={`member-tooltip-${user_id}`}
                                                  data-tip="The user has a Certificate of Attendance."
                                                  alt=""
                                                />)}
                                              {member_data?.place && (
                                                <span className={styles.member_place}>
                                                  {member_data?.place}
                                                </span>
                                              )}
                                            </>
                                            <>
                                              {member_data?.certificate_of_attendance_sent && (
                                                <a>
                                                  <img
                                                    src="/icons/email_40x40.png"
                                                    className={styles.membericon}
                                                    data-for="tooltip"
                                                    data-tip="The Certificate of Attendance has been sent by email.<br/>Click here to resend the link from your personal email account."
                                                    alt=""
                                                  />
                                                </a>
                                              )}
                                            </>

                                            <>
                                              {props.cohort.countrycode === "HU" && member_data?.sent_far_info && (
                                                <img
                                                  src={!!member_data.far ? "/icons/homework_40x40_2.png" : "/icons/homework_40x40.png" }
                                                  className={styles.membericon_after}
                                                  data-for="tooltip"
                                                  data-tip={!!member_data.far ? 
                                                                      `The information gathering form for the FAR (HU) has been filled${member_data.far?.filled_at ? " at " + new Date(member_data.far.filled_at * 1000).toLocaleString() : ""}.` 
                                                                      : 
                                                                      `The information gathering form for the FAR (HU) has been sent${member_data.sent_far_info ? " at " + new Date(member_data.sent_far_info * 1000).toLocaleString() : ""}.` }
                                                  alt=""
                                                />)}
                                            </>

                                            <>
                                              {member_data?.action_accomplished && (
                                                <span>&nbsp;
                                                  <img
                                                    src="/icons/OK_40x40_2.png"
                                                    className={styles.membericon_after}
                                                    data-for="tooltip"
                                                    data-tip="The action has been accomplished."
                                                    alt=""
                                                  />
                                                </span>)}
                                            </>
                                            <>
                                              {member_data?.action_errored && (
                                                <span>&nbsp;
                                                  <img
                                                    src="/icons/error_40x40.png"
                                                    className={styles.membericon_after}
                                                    data-for="tooltip"
                                                    data-tip="The action has errored."
                                                    alt=""
                                                  />
                                                </span>)}
                                            </>
                                          </span>
                                        </li>
                                      );
                                    }
                                  })}
                                </Fragment>
                              )
                            })}
                          </>
                        )}
                      </Loading>
                    </ul>
                  </div>

                  <div className={styles.members_controls}>
                    <div className="divider"></div>
                    {!!hasPermission(props.userInfo, [{ permission: "cohort_write", resource: props.cohort.id }]) ? (
                      <div>
                        <form onSubmit={addUsersSubmitHandler}>
                          <div className="cy__flex_container">
                            <div className="cy__flex_row cy__flex_gap_15px">
                              <div className={`cy__flex_container`} style={{height: "fit-content"}}>
                                <textarea
                                  className={styles.member_emails}
                                  value={emailInput.value}
                                  onChange={emailInput.valueChangeHandler}
                                  onBlur={emailInput.inputBlurHandler}
                                  id="email"
                                  placeholder="member@email.com"
                                  required
                                />
                              </div>
                              {addMemberApiState.isLoading && addedUserNumber<addUserCount && (
                                <span className={styles.added_user_count}>
                                  Added {addedUserNumber}/{addUserCount}
                                </span>
                              )}
                              <div className={`cy__flex_container ${styles.member_add}`}>
                                <Loading isLoading={addMemberApiState.isLoading} className={styles.member_loading}>
                                  <button
                                    type="submit"
                                    className={`btn secondary wide  ${emailInput.isValid ? "" : "disabled"}`}
                                  >
                                  Add user(s)
                                  </button>
                                </Loading>
                              </div>
                              {!!addMemberApiState.errorMessage && (
                                <div>
                                  <ErrorNotification
                                    errorMessage={addMemberApiState.errorMessage}
                                  ></ErrorNotification>
                                </div>
                              )}
                              <Loading isLoading={getAllCertificateLinksForUserApiState.isLoading} className={styles.clipboard_icon_loading}>

                                <div className={styles.all_certificate_link_clipboard_icon_div}
                                    onClick={() => copyAllCertificatesToClipboard()}>
                                  <img
                                    src="/icons/gamification_40x40.png"
                                    className={`${styles.membericon_after} ${styles.all_certificate_link_clipboard_cert_icon}`}
                                    alt=""
                                  />
                                  <img
                                      src="/icons/copy.svg"
                                      className={`${styles.membericon} ${styles.all_certificate_link_clipboard_copy_icon}`}
                                      data-for="tooltip"
                                      data-tip="Copy all certificate links to clipboard."
                                      alt=""
                                      style={{cursor:'pointer'}}
                                  />
                                </div>

                                <div className={styles.all_registration_link_clipboard_icon_div}
                                    onClick={() => copyAllEmailsToClipboard()}>
                                  <img
                                    src="/icons/email_40x40_2.png"
                                    className={`${styles.membericon_after} ${styles.all_registration_link_clipboard_reg_icon}`}
                                    alt=""
                                  />
                                  <img
                                    src="/icons/copy.svg"
                                    className={`${styles.membericon} ${styles.all_registration_link_clipboard_copy_icon}`}
                                    data-for="tooltip"
                                    data-tip="Copy all active registration links to clipboard."
                                    alt=""
                                    style={{cursor:'pointer'}}
                                  />
                                </div>

                                { hasPermission(props.userInfo, [{permission: "cohort_write", resource: props.cohort.id}]) && 
                                  (
                                  <div className={styles.all_far_data_clipboard_icon_div}
                                      onClick={() => copyAllFarDataToClipboard()}>
                                    <img
                                      src="/icons/homework_40x40_2.png"
                                      className={`${styles.membericon_after} ${styles.all_far_data_clipboard_reg_icon}`}
                                      alt=""
                                    />
                                    <img
                                      src="/icons/copy.svg"
                                      className={`${styles.membericon} ${styles.all_far_data_clipboard_copy_icon}`}
                                      data-for="tooltip"
                                      data-tip="Copy all FAR data to clipboard."
                                      alt=""
                                      style={{cursor:'pointer'}}
                                    />
                                  </div>
                                  )
                                }
                              </Loading>
                            </div>
                          </div>
                        </form>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          }
          bottomPanel={
            <>
              <div className={styles.bottom__panel}>
                {hasPermission(props.userInfo, [
                  { permission: "cohort_communication", resource: props.cohort.id }
                ]) && (
                    <div className="buttons">
                      <Loading isLoading={sendRegistrationEmailApiState.isLoading}>
                        <ErrorNotification
                          errorMessage={sendRegistrationEmailApiState.errorMessage}
                        />
                        <button
                          onClick={sendRegistrationEmail}
                          className="btn primary wide"
                          disabled={numberOfCheckedUser() === 0}
                          title={"Send registration emails to the selected users and send an email to them (even if already sent)."}
                        >
                          Send registration email
                        </button>
                      </Loading>

                      {props.cohort.countrycode === "HU" && hasPermission(props.userInfo, [
                          { permission: "cohort_write", resource: props.cohort.id }
                        ]) &&(
                        <Loading isLoading={sendAdultTrainingHuInfoEmailApiState.isLoading}>
                          <ErrorNotification
                            errorMessage={sendAdultTrainingHuInfoEmailApiState.errorMessage}
                          />
                          <button
                            onClick={sendAdultTrainingHuInfoEmail}
                            className={`${styles.secondButton} btn primary wide`}
                            disabled={numberOfCheckedUser() === 0}
                            title={"Send FAR (HU) info gathering by email to selected users (even if already sent)."}
                          >
                            Send FAR (HU) email
                          </button>
                        </Loading>
                      )}

                    </div>
                  )}

                {hasPermission(props.userInfo, [
                  { permission: "cohort_communication", resource: props.cohort.id }
                ]) ? (
                  <div className="buttons">

                    <Loading isLoading={sendEvalformEmailApiState.isLoading}>
                      <ErrorNotification
                        errorMessage={sendEvalformEmailApiState.errorMessage}
                      />
                      <button
                        onClick={sendEvalformEmail}
                        className="btn primary wide"
                        disabled={numberOfCheckedUser() === 0}
                        title={"Send course evaluation form by email to selected users (even if already sent)."}
                      >
                        Send course evaluation form by email
                      </button>
                    </Loading>

                  </div>
                ) : null}

                {hasPermission(props.userInfo, [
                  { permission: "cohort_write", resource: props.cohort.id }
                ]) ? (
                  <div className="buttons">
                    <Loading isLoading={generateCertificateApiState.isLoading}>
                      <ErrorNotification
                        errorMessage={generateCertificateApiState.errorMessage}
                      />
                      <button
                        onClick={generateCertificateForCheckedUsers}
                        className="btn primary wide"
                        disabled={numberOfCheckedUser() === 0}
                      >
                        Generate certificate
                      </button>
                    </Loading>

                    <Loading isLoading={sendCertificateEmailApiState.isLoading}>
                      <ErrorNotification
                        errorMessage={sendCertificateEmailApiState.errorMessage}
                      />
                      <button
                        onClick={sendCertificateEmail}
                        className={`${styles.secondButton} btn primary wide`}
                        title={"Send certificate by email to selected users (even if already sent)."}
                        disabled={numberOfCheckedUser() === 0}
                      >
                        Send certificate by email
                      </button>
                    </Loading>

                  </div>
                ) : null}

                {hasPermission(props.userInfo, [
                  { permission: "cohort_write", resource: props.cohort.id }
                ]) && (
                    <div className="buttons">
                      <Loading
                        isLoading={getMemberRegistrationStatusApiState.isLoading}
                      >
                        {getMemberRegistrationStatusApiState.errorMessage !== null ? (
                          <ErrorNotification
                            errorMessage={getMemberRegistrationStatusApiState.errorMessage}
                          />
                        ) : (
                          <button
                            onClick={checkMembersRegistration}
                            className="btn primary wide"
                            disabled={numberOfCheckedUser() === 0}
                          >
                            Check learning modules
                          </button>
                        )}
                      </Loading>
                    </div>
                  )}

                {hasPermission(props.userInfo, [
                  { permission: "cohort_write", resource: props.cohort.id }
                ]) && selectedUserUserId !== null && (
                    <>
                      <Loading isLoading={getCertificateLinkForUserApiState.isLoading}>
                        {getCertificateLinkForUserApiState.errorMessage !== null ? (
                          <ErrorNotification
                            errorMessage={getCertificateLinkForUserApiState.errorMessage}
                          />
                        ) : (
                          <>
                            {selectedUserCertificateLink && (
                              <>
                                <div className="cy__link">
                                  <div className="link" id="pdflink">
                                    <a
                                      href={selectedUserCertificateLink}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {selectedUserCertificateLink}
                                    </a>
                                    <img
                                      src="/icons/copy.svg"
                                      className={styles.copyicon}
                                      title={decodeURI("Copy the link to clipboard.")}
                                      alt=""
                                      onClick={() => navigator.clipboard.writeText(selectedUserCertificateLink)}
                                      style={{cursor:'pointer'}}
                                    />
                                  </div>
                                </div>
                                <div className="cy__pdfpreview">
                                  <embed
                                    type="application/pdf"
                                    // frameBorder="0"
                                    // scrolling="auto"
                                    height="100%"
                                    width="100%"
                                    src={selectedUserCertificateLink}
                                  ></embed>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </Loading>
                    </>
                  )}
              </div>
            </>
          }
        />
      </div>
    </>
  );
}
